import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import type { ApprovalRequest } from '@refdash/api-client';
import { cn, getPatientName, getUrgencyLevel } from '@/lib/utils';
import { useCountdown } from '@/hooks/use-countdown';
import { CountdownBadge } from './countdown-badge';

interface ApprovalCardProps {
  approval: ApprovalRequest;
  index: number;
}

const urgencyBarClass: Record<string, string> = {
  calm: 'urgency-bar-calm',
  watch: 'urgency-bar-watch',
  urgent: 'urgency-bar-urgent',
  critical: 'urgency-bar-critical',
  // Past timer but still open on GlobeMed — still decidable
  overdue: 'urgency-bar-critical',
};

export function ApprovalCard({ approval, index }: ApprovalCardProps) {
  const navigate = useNavigate();
  const secondsLeft = useCountdown(approval.timer_expires_at);
  const urgency = getUrgencyLevel(secondsLeft);

  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: index * 0.06,
        duration: 0.35,
        ease: [0.22, 1, 0.36, 1],
      }}
      onClick={() => navigate(`/approvals/${approval.id}`)}
      className={cn(
        'w-full text-left rounded-2xl bg-card border border-border shadow-xs',
        'flex overflow-hidden',
        'transition-all duration-150',
        'hover:shadow-md hover:border-stone-300',
        'active:scale-[0.98]',
        'cursor-pointer',
        'group',
      )}
    >
      {/* Urgency bar */}
      <div className={cn('w-1.5 shrink-0', urgencyBarClass[urgency])} />

      {/* Content */}
      <div className="flex-1 flex items-center justify-between p-4 pl-4 gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[11px] text-muted-foreground tracking-wide uppercase mb-1">
            {approval.ihalaty_reference}
          </p>
          <p className="font-sans text-base font-bold text-foreground truncate leading-tight">
            {getPatientName(approval.patient_info)}
          </p>
          {approval.patient_info.required_specialty && (
            <p className="text-sm text-muted-foreground mt-0.5 truncate">
              {approval.patient_info.required_specialty as string}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {approval.origin === 'direct' ? (
            // Direct-intake cases have no GlobeMed race timer — show their
            // provenance + triage priority instead of a meaningless countdown.
            <span className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-semibold',
              approval.priority === 'emergency' ? 'bg-red-100 text-red-700'
                : approval.priority === 'urgent' ? 'bg-amber-100 text-amber-700'
                : 'bg-stone-100 text-stone-600',
            )}>
              Direct{approval.priority && approval.priority !== 'routine' ? ` · ${approval.priority}` : ''}
            </span>
          ) : (
            <CountdownBadge
              expiresAt={approval.timer_expires_at}
              size="sm"
              showIcon={false}
            />
          )}
          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </div>
      </div>
    </motion.button>
  );
}
